import React, { createContext, useContext, useEffect, useMemo, useCallback } from 'react';
import { TRANSLATIONS, DEFAULT_LANG, detectLang } from '../lib/i18n';
import { usePersistedState } from '../hooks/usePersistedState';
import { useAuth } from './AuthContext';
import { api } from '../lib/api';

const AppContext = createContext(null);

export const AppProvider = ({ children }) => {
  const { user } = useAuth();
  const [lang, setLang] = usePersistedState('kushe_lang', detectLang);
  const [currency, setCurrency] = usePersistedState('kushe_currency', 'XOF');
  const [country, setCountry] = usePersistedState('kushe_country', 'SN');
  const [favorites, setFavorites] = usePersistedState('kushe_favs', []);

  useEffect(() => {
    if (!user) return;
    const local = JSON.parse(localStorage.getItem('kushe_favs') || '[]');
    api.put('/favorites/sync', local)
      .then((r) => setFavorites(r.data))
      .catch((e) => console.error('Favorites sync failed', e));
  }, [user, setFavorites]);

  const t = useCallback((key) => TRANSLATIONS[lang]?.[key] || TRANSLATIONS[DEFAULT_LANG][key] || key, [lang]);
  const toggleFav = useCallback((id) => {
    setFavorites((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
    if (user) api.post(`/favorites/${id}`).catch((e) => console.error('Favorite toggle failed', e));
  }, [setFavorites, user]);
  const isFav = useCallback((id) => favorites.includes(id), [favorites]);

  const value = useMemo(
    () => ({ lang, setLang, currency, setCurrency, country, setCountry, t, favorites, toggleFav, isFav }),
    [lang, setLang, currency, setCurrency, country, setCountry, t, favorites, toggleFav, isFav],
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error('useApp must be inside AppProvider');
  return ctx;
};
